import { ApplicantStage, HiringStage } from 'src/graphql/types/applicants'
import { AutomatedRuleConditionKey } from 'src/graphql/types/automatedRules'

export type StageKey =
  | 'LEAD'
  | 'APPLICATION'
  | 'QUALIFICATION'
  | 'ONBOARDING'
  | 'HIRED'
  | 'NOT_INTERESTED'
  | 'DISQUALIFIED'
  | 'TERMINATED'
  | 'UNSCOPED'

// In-progress stages — top row of the pipeline grid.
export const MAIN_STAGE_KEYS: StageKey[] = [
  'LEAD',
  'APPLICATION',
  'QUALIFICATION',
  'ONBOARDING',
]

// Terminal outcomes — bottom row. HIRED is the positive outcome, the rest
// are off-ramps. Ordered so the positive outcome reads first.
export const OUTCOME_STAGE_KEYS: StageKey[] = [
  'HIRED',
  'NOT_INTERESTED',
  'DISQUALIFIED',
  'TERMINATED',
]

const OFF_RAMP_APPLICANT_STAGES = new Set<string>([
  ApplicantStage.NOT_INTERESTED,
  ApplicantStage.DISQUALIFIED,
  ApplicantStage.TERMINATED,
])

// HIRED is an applicantStage value (not a hiringStage), so it has its own
// resolution path separate from the four hiringStage-keyed main lanes.
const HIRED_APPLICANT_STAGE = ApplicantStage.HIRED as string

type ConditionLike = {
  key: string
  value?: string | null
}

type RuleLike<C extends ConditionLike = ConditionLike> = {
  conditions: C[]
}

const findFirstValue = (
  conditions: ConditionLike[],
  key: string
): string | null => {
  const match = conditions.find((c) => c.key === key)
  return match?.value ?? null
}

export const resolveStageKey = (conditions: ConditionLike[]): StageKey => {
  const applicantStage = findFirstValue(
    conditions,
    AutomatedRuleConditionKey.applicantStage
  )

  if (applicantStage && OFF_RAMP_APPLICANT_STAGES.has(applicantStage)) {
    return applicantStage as StageKey
  }

  if (applicantStage === HIRED_APPLICANT_STAGE) {
    return 'HIRED'
  }

  const hiringStage = findFirstValue(
    conditions,
    AutomatedRuleConditionKey.hiringStage
  )
  if (hiringStage && (Object.values(HiringStage) as string[]).includes(hiringStage)) {
    return hiringStage as StageKey
  }

  return 'UNSCOPED'
}

const emptyBuckets = (): Record<StageKey, RuleLike[]> => ({
  LEAD: [],
  APPLICATION: [],
  QUALIFICATION: [],
  ONBOARDING: [],
  HIRED: [],
  NOT_INTERESTED: [],
  DISQUALIFIED: [],
  TERMINATED: [],
  UNSCOPED: [],
})

export const bucketRulesByStage = <R extends RuleLike>(
  rules: R[]
): Record<StageKey, R[]> => {
  const buckets = emptyBuckets() as Record<StageKey, R[]>
  for (const rule of rules) {
    const key = resolveStageKey(rule.conditions)
    buckets[key].push(rule)
  }
  return buckets
}

// Conditions of the same key (e.g. three linkType rows representing
// "Indeed OR Facebook OR Craigslist") collapse to a single chip with
// comma-joined values. Step conditions stay one-per-step because each
// represents a different step type.
export const collapseConditionsByKey = <C extends ConditionLike>(
  conditions: C[]
): C[] => {
  const collapsed: C[] = []
  const indexByKey = new Map<string, number>()

  for (const condition of conditions) {
    if (condition.key === AutomatedRuleConditionKey.applicantStage) continue
    if (condition.key === AutomatedRuleConditionKey.hiringStage) continue
    if (condition.key === 'step') {
      collapsed.push(condition)
      continue
    }
    const existingIndex = indexByKey.get(condition.key)
    if (existingIndex === undefined) {
      indexByKey.set(condition.key, collapsed.length)
      collapsed.push({ ...condition })
      continue
    }
    const existing = collapsed[existingIndex]
    const merged =
      existing.value && condition.value
        ? `${existing.value}, ${condition.value}`
        : existing.value || condition.value || ''
    collapsed[existingIndex] = { ...existing, value: merged }
  }

  return collapsed
}

// Rules within a lane sort to read chronologically — the way a recruiter
// would mentally walk the drip:
//   1. Immediate (text + email on entry)
//   2. The middle of the drip — scheduled rules and event-triggered rules
//      interleaved by their effective firing time
//   3. Recurring "background" drips (DAILY/WEEKLY) at the bottom
//
// Event-triggered rules don't have a literal delay, so we approximate
// their position: a rule conditioned on callCount=N realistically fires
// around N minutes in (≈ one call attempt per minute on average), and a
// `missed` rule lands one tick after the non-missed rule at the same N.
// This puts the typical "if missed call 1, send a text" rule between a
// "1 min call" and "5 min call" rule, which matches how clients describe
// their flows. It is a heuristic — drips with non-standard cadences will
// still sort in roughly the right shape but not pixel-perfect.
const EVENT_CONDITION_KEYS = new Set<string>([
  'step',
  AutomatedRuleConditionKey.callCount,
  AutomatedRuleConditionKey.missedOutboundCallCount,
])

const APPROX_SECONDS_PER_CALL_ATTEMPT = 60

type ScheduleAndConditionsLike = {
  conditions: ConditionLike[]
  triggerDelay?: number | null
  frequency?: string | null
}

const TIER_IMMEDIATE = 0
const TIER_BODY = 1
const TIER_RECURRING = 2

const isRecurring = (rule: ScheduleAndConditionsLike): boolean =>
  rule.frequency != null && rule.frequency !== 'ONCE'

const hasEventCondition = (rule: ScheduleAndConditionsLike): boolean =>
  rule.conditions.some((c) => EVENT_CONDITION_KEYS.has(c.key))

const parseCount = (value: string | null | undefined): number => {
  if (!value) return 0
  // Multi-value conditions store comma-joined strings; take the highest
  // count, since a `callCount IN (1,2,3)` rule effectively fires at the
  // latest of those points.
  const parts = value.split(',').map((v) => parseInt(v.trim(), 10))
  const valid = parts.filter((n) => !Number.isNaN(n))
  return valid.length ? Math.max(...valid) : 0
}

// Returns the "effective firing time" in seconds for sorting. Lower = earlier.
const effectiveDelaySeconds = (rule: ScheduleAndConditionsLike): number => {
  const baseDelay = rule.triggerDelay ?? 0

  const callCondition = rule.conditions.find(
    (c) => c.key === AutomatedRuleConditionKey.callCount
  )
  const missedCondition = rule.conditions.find(
    (c) => c.key === AutomatedRuleConditionKey.missedOutboundCallCount
  )

  if (callCondition || missedCondition) {
    const count = Math.max(
      parseCount(callCondition?.value),
      parseCount(missedCondition?.value)
    )
    // `+1` on missed so it sorts just after the non-missed rule at the
    // same call count.
    const missedTiebreak = missedCondition ? 1 : 0
    return count * APPROX_SECONDS_PER_CALL_ATTEMPT + missedTiebreak + baseDelay
  }

  // Step events fall back to their literal triggerDelay — no count to
  // approximate against.
  return baseDelay
}

export const ruleExecutionPriority = (
  rule: ScheduleAndConditionsLike
): number => {
  const recurring = isRecurring(rule)
  const event = hasEventCondition(rule)
  const delayed = rule.triggerDelay != null && rule.triggerDelay > 0

  if (!event && !delayed && !recurring) return TIER_IMMEDIATE
  // Recurring drips with no real delay belong at the end as "background"
  // automations. A recurring rule with a real triggerDelay still acts on
  // a specific moment in the drip, so we keep it in the body.
  if (recurring && !delayed && !event) return TIER_RECURRING
  return TIER_BODY
}

export const sortRulesByExecutionPriority = <
  R extends ScheduleAndConditionsLike
>(
  rules: R[]
): R[] =>
  [...rules]
    .map((rule, index) => ({ rule, index }))
    .sort((a, b) => {
      const tierDiff =
        ruleExecutionPriority(a.rule) - ruleExecutionPriority(b.rule)
      if (tierDiff !== 0) return tierDiff
      const timeDiff =
        effectiveDelaySeconds(a.rule) - effectiveDelaySeconds(b.rule)
      if (timeDiff !== 0) return timeDiff
      // Stable fallback so equal-keyed rules keep their incoming order.
      return a.index - b.index
    })
    .map(({ rule }) => rule)

const PRIORITY_ORDER: Record<string, number> = {
  [AutomatedRuleConditionKey.jobListingType]: 1,
  [AutomatedRuleConditionKey.jobListingId]: 2,
  [AutomatedRuleConditionKey.recruiterId]: 2,
  [AutomatedRuleConditionKey.linkType]: 3,
  [AutomatedRuleConditionKey.tagId]: 3,
  step: 4,
  [AutomatedRuleConditionKey.callCount]: 5,
  [AutomatedRuleConditionKey.missedOutboundCallCount]: 5,
  [AutomatedRuleConditionKey.notInterestedReason]: 6,
  [AutomatedRuleConditionKey.disqualifiedReason]: 6,
  [AutomatedRuleConditionKey.terminatedReason]: 6,
}

const FALLBACK_PRIORITY = 99

export const sortConditionsByPriority = <C extends ConditionLike>(
  conditions: C[]
): C[] =>
  [...conditions].sort((a, b) => {
    const pa = PRIORITY_ORDER[a.key] ?? FALLBACK_PRIORITY
    const pb = PRIORITY_ORDER[b.key] ?? FALLBACK_PRIORITY
    return pa - pb
  })
